import type { FC, ReactElement } from 'react';

import { selectAssetVersionsExtendedMap } from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import { Icon } from '@iconik-io/uad';
import type { UploaderState } from '@iconik-io/uploader/src/state';

import TooltipTrigger from '../../components/tooltip_trigger/tooltip_trigger';
import {
  UPLOADER_ACLS_MODAL,
  UPLOADER_EDIT_METADATA_MODAL,
} from '../../constants/modals';
import { openModal } from '../../ducks/modals_manager/slice';
import {
  getUploadAssetId,
  getUploadFormatName,
  getUploadVersionId,
  isPlaceholderUpload,
  isProxyUpload,
  isSubtitleUpload,
  isVersionUpload,
} from '../../ducks/uploader/upload_state';
import { useLeafRouteParams } from '../../router/use_leaf_route_params';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import { getUploaderFilePath } from './helpers';
import { selectIsUploaderFullScreen } from './selectors';

import styles from './uploads_panel_widget.module.css';

type Props = {
  item: UploaderState;
};

const FileIcons: FC<Props> = ({ item }) => {
  const { route, params } = useLeafRouteParams();
  const icons: ReactElement[] = [];
  const dispatch = useAppDispatch();
  const path = useAppSelector((state) => getUploaderFilePath(state, item));
  const storagesEntities = useAppSelector((state) => state.entities.storages);
  const assetEntities = useAppSelector((state) => state.entities.assets);
  const assetId = getUploadAssetId(item);
  const storageId = item.options.storageId;
  const formatName = getUploadFormatName(item);
  const versionsMap = useAppSelector((state) =>
    selectAssetVersionsExtendedMap(state, assetId),
  );
  const storageMatching = useAppSelector(
    (state) =>
      state.uploaderReducer.selectedStorage ||
      state.storageMatchingReducer.filesStorageMatched?.id,
  );
  const metadataViews = useAppSelector(
    (state) => state.entities.metadata_views,
  );
  const uploaderMetadata = useAppSelector(
    (state) => state.uploaderMetadata[item.id],
  );
  const hasAcls = useAppSelector((state) =>
    Boolean(state.uploaderAcls[item.id]),
  );
  const fullscreen = useAppSelector((state) =>
    selectIsUploaderFullScreen(state, params.id, params.versionId, route),
  );

  if (assetId && assetEntities[assetId]) {
    const asset = assetEntities[assetId];

    if (isPlaceholderUpload(item)) {
      // placeholder icon
      icons.push(
        <TooltipTrigger
          tooltipText={`To Placeholder: ${asset.title}`}
          key="placeholder"
        >
          <Icon name="asset_type_placeholder" />
        </TooltipTrigger>,
      );
    }
    // proxy icon
    if (isProxyUpload(item)) {
      icons.push(
        <TooltipTrigger tooltipText={`Proxy: ${asset.title}`} key="proxy">
          <Icon name="file_proxy" />
        </TooltipTrigger>,
      );
    }
    // version icon
    if (isVersionUpload(item)) {
      const versionId = getUploadVersionId(item);
      icons.push(
        <TooltipTrigger
          tooltipText={`${(versionId && versionsMap?.[versionId]?.name) || 'Version'}: ${
            asset.title
          }`}
          key="version"
        >
          <Icon name="versions_add_new" />
        </TooltipTrigger>,
      );
    }
    // subtitles icon
    if (isSubtitleUpload(item)) {
      icons.push(
        <TooltipTrigger
          tooltipText={`Subtitles: ${asset.title}`}
          key="subtitles"
        >
          <Icon name="subtitles" />
        </TooltipTrigger>,
      );
    }
    // format icon
    if (formatName) {
      icons.push(
        <TooltipTrigger
          tooltipText={`${formatName}: ${asset.title}`}
          key="format"
        >
          <Icon name="transcoder" />
        </TooltipTrigger>,
      );
    }
  }

  if (
    storageId &&
    storageId !== storageMatching &&
    storagesEntities[storageId]
  ) {
    icons.push(
      <TooltipTrigger
        tooltipText={`Uploading to: ${
          storagesEntities[storageId]?.name ?? 'Storage'
        }`}
        key="storage"
      >
        <Icon name="storage_cloud" />
      </TooltipTrigger>,
    );
  }
  if (path && !fullscreen) {
    icons.push(
      <TooltipTrigger tooltipText={path} key="collection">
        <Icon name="collection" />
      </TooltipTrigger>,
    );
  }

  // metadata icon
  if (uploaderMetadata) {
    icons.push(
      <TooltipTrigger
        tooltipText={metadataViews[uploaderMetadata.view].name ?? 'Metadata'}
        key="metadata"
      >
        <button
          type="button"
          className={styles.file_icon_button}
          onClick={() => {
            dispatch(
              openModal({
                id: UPLOADER_EDIT_METADATA_MODAL,
                contextItems: [item],
              }),
            );
          }}
        >
          <Icon name="Svg2022_uad_metadata" />
        </button>
      </TooltipTrigger>,
    );
  }
  // acls icon
  if (hasAcls) {
    icons.push(
      <TooltipTrigger tooltipText="Permissions" key="acls">
        <button
          type="button"
          className={styles.file_icon_button}
          onClick={() => {
            dispatch(
              openModal({
                id: UPLOADER_ACLS_MODAL,
                contextItems: [item],
              }),
            );
          }}
        >
          <Icon name="access_control" />
        </button>
      </TooltipTrigger>,
    );
  }
  return <div className={styles.file_icons}>{icons}</div>;
};

export default FileIcons;
